import { useEffect, useState } from "react";
import { Handshake, FileSearch2, Trophy, Banknote } from "lucide-react";
import { api } from "@/lib/api";

const formatFJD = (n) => {
    if (!n || n <= 0) return "FJ$0";
    if (n >= 1_000_000) return `FJ$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M+`;
    if (n >= 1_000) return `FJ$${Math.round(n / 1_000)}k+`;
    return `FJ$${n}`;
};

const formatCount = (n) => {
    if (n == null || n <= 0) return "0";
    return `${n}`;
};

function PulseStat({ icon: Icon, label, value, formatter, testid, accent = "fiji-green" }) {
    const tones = {
        "fiji-green": "bg-fiji-green/15 text-fiji-green",
        "ocean": "bg-ocean/15 text-ocean dark:bg-turquoise/15 dark:text-turquoise",
        "coral": "bg-coral/15 text-coral",
        "turquoise": "bg-turquoise/15 text-turquoise",
    };
    return (
        <div
            data-testid={testid}
            className="flex items-center gap-3 px-4 sm:px-5 py-3.5 sm:py-4 rounded-xl bg-card/80 backdrop-blur border border-border hover:border-coral/60 transition-colors"
        >
            <div className={`h-10 w-10 rounded-lg ${tones[accent] || tones["fiji-green"]} grid place-items-center flex-shrink-0`}>
                <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
                <div className="font-display text-xl sm:text-2xl font-bold tabular-nums text-foreground leading-none">
                    {formatter(value)}
                </div>
                <div className="mt-1 text-[11px] sm:text-xs uppercase tracking-[0.18em] text-muted-foreground font-app font-medium">
                    {label}
                </div>
            </div>
        </div>
    );
}

export default function DealPulse() {
    const [pulse, setPulse] = useState(null);

    useEffect(() => {
        let mounted = true;
        const fetchPulse = async () => {
            try {
                const { data } = await api.get("/public/deal-pulse");
                if (mounted) setPulse(data);
            } catch (_e) {
                /* fail silent — widget hides on API failure */
            }
        };
        fetchPulse();
        const interval = setInterval(fetchPulse, 60_000);
        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, []);

    // Hide if no deals are in pipeline yet — keeps the homepage clean pre-launch
    if (!pulse) return null;
    const totalSignal = (pulse.deals_introduced_quarter || 0) + (pulse.in_diligence || 0) + (pulse.closed_deals_total || 0);
    if (totalSignal === 0) return null;

    return (
        <section
            data-testid="deal-pulse-section"
            className="relative px-4 sm:px-6 lg:px-8 py-12 bg-gradient-to-br from-ocean-deep/[0.04] to-coral/[0.04] dark:from-ocean-deep/30 dark:to-coral/[0.06]"
        >
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center gap-2 mb-2">
                    <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-coral opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-coral" />
                    </span>
                    <span className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-app font-semibold text-coral dark:text-coral">
                        Deal Pipeline · Last 90 days
                    </span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold mb-2 text-foreground">
                    Real deals, moving in real time.
                </h2>
                <p className="font-app text-muted-foreground max-w-2xl mb-6">
                    Every match starts with a lead, builds through diligence, and closes as a signed Joint Venture or sale. Here's what's in motion right now across the Bula marketplace.
                </p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <PulseStat
                        icon={Handshake}
                        label="Introduced this quarter"
                        value={pulse.deals_introduced_quarter}
                        formatter={formatCount}
                        testid="deal-pulse-introduced"
                        accent="fiji-green"
                    />
                    <PulseStat
                        icon={Banknote}
                        label="Pipeline value (90d)"
                        value={pulse.introduced_value_quarter_fjd}
                        formatter={formatFJD}
                        testid="deal-pulse-value"
                        accent="ocean"
                    />
                    <PulseStat
                        icon={FileSearch2}
                        label="In diligence"
                        value={pulse.in_diligence}
                        formatter={formatCount}
                        testid="deal-pulse-diligence"
                        accent="coral"
                    />
                    <PulseStat
                        icon={Trophy}
                        label="Closed all-time"
                        value={pulse.closed_deals_total}
                        formatter={formatCount}
                        testid="deal-pulse-closed"
                        accent="turquoise"
                    />
                </div>
            </div>
        </section>
    );
}
